import { useMutation, useQueryClient } from '@tanstack/react-query'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { toast } from 'sonner'

export function useGenerateValuation() {
  const qc = useQueryClient()
  const fn = httpsCallable<{ slug: string }, { ok: boolean; valuation?: unknown; error?: string }>(
    functions, 'generateValuation'
  )
  return useMutation({
    mutationFn: async (slug: string) => (await fn({ slug })).data,
    onSuccess: (data, slug) => {
      if (data.ok) {
        toast.success(`Valuation generated for ${slug}`)
        qc.invalidateQueries({ queryKey: ['apps'] })
      } else {
        toast.error(data.error ?? 'Valuation failed')
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useGenerateWhitepaper() {
  const qc = useQueryClient()
  const fn = httpsCallable<{ slug: string }, { ok: boolean; gamma_url?: string; gamma_id?: string; error?: string }>(
    functions, 'generateWhitepaper'
  )
  return useMutation({
    mutationFn: async (slug: string) => (await fn({ slug })).data,
    onSuccess: (data, slug) => {
      if (data.ok) {
        toast.success(`Whitepaper deck generated for ${slug}`)
        qc.invalidateQueries({ queryKey: ['apps'] })
      } else {
        toast.error(data.error ?? 'Generation failed')
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useGenerateMarketingDeck() {
  const qc = useQueryClient()
  const fn = httpsCallable<{ slug: string }, { ok: boolean; gamma_url?: string; gamma_id?: string; error?: string }>(
    functions, 'generateMarketingDeck'
  )
  return useMutation({
    mutationFn: async (slug: string) => (await fn({ slug })).data,
    onSuccess: (data, slug) => {
      if (data.ok) {
        toast.success(`Marketing deck generated for ${slug}`)
        qc.invalidateQueries({ queryKey: ['apps'] })
      } else {
        toast.error(data.error ?? 'Generation failed')
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useGenerateValuationDoc() {
  const qc = useQueryClient()
  const fn = httpsCallable<{ slug: string }, { ok: boolean; google_doc_url?: string; google_doc_id?: string; drive_folder_url?: string | null; error?: string }>(
    functions, 'generateValuationDoc'
  )
  return useMutation({
    mutationFn: async (slug: string) => (await fn({ slug })).data,
    onSuccess: (data, slug) => {
      if (data.ok) {
        toast.success(`Branded valuation doc generated for ${slug}`)
        qc.invalidateQueries({ queryKey: ['apps'] })
      } else {
        toast.error(data.error ?? 'Generation failed')
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function usePushToVault() {
  const qc = useQueryClient()
  const fn = httpsCallable<
    { slug: string; kind: 'valuation' | 'whitepaper' | 'marketing_deck' },
    { ok: boolean; document_id?: string; vault_url?: string; error?: string }
  >(functions, 'pushToVault')
  return useMutation({
    mutationFn: async (input: { slug: string; kind: 'valuation' | 'whitepaper' | 'marketing_deck' }) => (await fn(input)).data,
    onSuccess: (data) => {
      if (data.ok) {
        toast.success(
          data.document_id
            ? `Pushed to ARH vault · ${data.document_id.slice(0, 8)}`
            : 'Pushed to ARH vault',
        )
        qc.invalidateQueries({ queryKey: ['apps'] })
      } else {
        toast.error(data.error ?? 'Push failed')
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useScanWorkspace() {
  const qc = useQueryClient()
  const fn = httpsCallable<{ paths?: string[] }, { ok: boolean; new_apps: number; updated_apps: number }>(
    functions, 'scanWorkspaceTrigger'
  )
  return useMutation({
    mutationFn: async (paths?: string[]) => (await fn({ paths: paths ?? [] })).data,
    onSuccess: (data) => {
      toast.success(`Scan complete · ${data.new_apps} new · ${data.updated_apps} updated`)
      qc.invalidateQueries({ queryKey: ['apps'] })
    },
    onError: (e: Error) => toast.error(e.message),
  })
}
